import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import HelpModal from './HelpModal';

interface HelpTriggerProps {
  /** Key that maps to contextual content or article slug */
  contentKey: string;
  /** Size of the help icon */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Optional custom label for accessibility */
  ariaLabel?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6'
};

/**
 * HelpTrigger - A clickable help icon that opens contextual help
 * 
 * Usage:
 * ```tsx
 * <label className="flex items-center gap-2">
 *   Bill Tracking
 *   <HelpTrigger contentKey="workspace-tracking" />
 * </label>
 * ```
 */
const HelpTrigger: React.FC<HelpTriggerProps> = ({
  contentKey,
  size = 'sm',
  className = '',
  ariaLabel
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={`inline-flex items-center justify-center text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full transition-colors ${className}`}
        aria-label={ariaLabel || `Help for ${contentKey.replace(/-/g, ' ')}`}
        title="Click for help"
      >
        <HelpCircle className={sizeClasses[size]} />
      </button>

      <HelpModal
        isOpen={isOpen}
        onClose={handleClose}
        contentKey={contentKey}
      />
    </>
  );
};

export default HelpTrigger;






